import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { getExpenses } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import StatusBadge from '../components/ui/StatusBadge'
import EmptyState from '../components/ui/EmptyState'

const EXP_STATUSES = ['ALL', 'SUBMITTED', 'APPROVED', 'REJECTED', 'REIMBURSED']

export default function Expenses() {
  const [allExpenses, setAllExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('ALL')

  useEffect(() => {
    setLoading(true)
    getExpenses({ take: 9999 })
      .then((res) => setAllExpenses(res.data.expenses || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    if (status === 'ALL') return allExpenses
    return allExpenses.filter((e) => e.status === status)
  }, [allExpenses, status])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(filtered, {
    defaultSort: 'date', defaultDir: 'desc', pageSize: 25, storageKey: 'expenses',
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
        <Link to="/expenses/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Expense
        </Link>
      </div>

      <div className="flex gap-1 flex-wrap">
        {EXP_STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${status === s ? 'bg-brand-100 text-brand-700' : 'text-gray-500 hover:bg-gray-100'}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden !p-0">
        {loading ? (
          <div className="space-y-3 p-6">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState type="expenses" message="No expenses found." actionLabel="Add an expense" actionTo="/expenses/new" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50/80 border-b">
                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Description" sortKey="description" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Category" sortKey="category.name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Amount" sortKey="amount" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((e) => (
                    <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-gray-600">{formatDate(e.date)}</td>
                      <td className="px-4 py-3">
                        <Link to={`/expenses/${e.id}`} className="text-brand-600 hover:underline font-medium line-clamp-1">
                          {e.description}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{e.category?.name || '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(e.amount)}</td>
                      <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
